/**
 * FirstRunQuestionnaireBanner — Dashboard nudge toward the paste flow.
 *
 * PR-P2-3 (UC-5 R2, cross-cluster UC-2 §7 friction point F7 — deep-link
 * parity for first-run nudges). Self-hides once the workspace has any
 * questionnaire at all, or once the user has dismissed it locally —
 * same self-hiding philosophy as NextDeadlineStrip / ReadinessTrendCard
 * (a nudge that never goes away isn't a nudge).
 *
 * Reduced scope: Artifact 1 §4.3 names a `first_run_banner_dismissals`
 * table (per-user, 30-day re-surface) that doesn't exist. Dismissal
 * here is localStorage — per-browser, permanent, not synced across
 * devices or re-surfaced after 30 days. Good enough for "don't nag
 * forever after the first click"; the real per-user/synced/timed
 * version needs the backend table, tracked as a follow-up.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Alert } from '@aigovops/design-system';
import { useQuery } from '@tanstack/react-query';
import { queryKeys } from '../../lib/query-keys';
import { listQuestionnaires } from '../../lib/api';

function dismissKey(slug: string): string {
  return `gacpro.dismissed.first-run-questionnaire-banner.${slug}`;
}

export function FirstRunQuestionnaireBanner({ slug }: { slug: string }) {
  const [dismissed, setDismissed] = useState(() => {
    try {
      return window.localStorage.getItem(dismissKey(slug)) === '1';
    } catch {
      return false;
    }
  });
  const stateQuery = useQuery({
    queryKey: queryKeys.questionnaires(slug),
    queryFn: () => listQuestionnaires(slug),
    enabled: !!slug,
  });
  const state = {
    data: stateQuery.data ?? null,
    error: stateQuery.error?.message ?? null,
    loading: stateQuery.isLoading,
  };

  if (dismissed || state.loading || state.error) return null;
  if ((state.data?.questionnaires.length ?? 0) > 0) return null;

  const dismiss = () => {
    try {
      window.localStorage.setItem(dismissKey(slug), '1');
    } catch {
      /* best-effort — a private-window user just sees it again next visit */
    }
    setDismissed(true);
  };

  return (
    <Alert variant="info" onDismiss={dismiss}>
      <strong>Paste a buyer questionnaire</strong> to get a draft response in minutes.
      For stronger, evidence-backed responses,{' '}
      <Link to={`/w/${slug}/app/evidence`} style={{ color: 'inherit', fontWeight: 700 }}>
        upload your policies first →
      </Link>{' '}
      or{' '}
      <Link to={`/w/${slug}/app/questionnaires/new`} style={{ color: 'inherit', fontWeight: 700 }}>
        paste a questionnaire now →
      </Link>
    </Alert>
  );
}
